import { useBankProgress } from "@/composables/useBankProgress";
import {
  applyStationProgress,
  isCurrentProgressSession,
} from "@/utils/bankProgress.mjs";
import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { useCollection, useFirestore, useCurrentUser } from "vuefire";
import { collection, where, query, getDocs } from "firebase/firestore";
import { useBankStore } from "./bank";
import {
  hasRecordedScore,
  sectionHasMarks,
  sectionSummaryHasMarks,
  stationHasMarks,
  stationSummaryCountsAsComplete,
  stationSummaryHasMarks,
} from "@/utils/oralSessionStatsHelpers.mjs";
import { computeVideoBankCategoryStats } from "@/utils/videoBankCategoryStats.mjs";

// import { useUserStore } from "./user";
// firebase functions
import { getFunctions, httpsCallable } from "firebase/functions";
// import { initializeApp } from "firebase/app";

// initializeApp();
import { firebaseApp } from "@/plugins/firebase"; // Adjust the path as needed

const functions = getFunctions(firebaseApp);
functions.region = "europe-west2";
// functions.region = "us-central1";
const CREATE_USER_SESSION = httpsCallable(functions, "createUserSession");
const NOTIFY_USER_PARENT_COMMENT = httpsCallable(
  functions,
  "notifyUserParentComment",
);
export const useDataStore = defineStore("data", () => {
  const db = useFirestore();
  const currentUser = useCurrentUser();
  const bankStore = useBankStore();
  const bankProgress = useBankProgress(computed(() => bankStore.currentBank)); // Initialize the bank store

  // const userStore = useUserStore();

  const totalInterviewStations = ref(0);
  const totalInterviewStationsByCategory = ref({});
  const totalVideoStations = ref(0);
  const totalVideoStationsByCategory = ref({});

  const validInterviewStationIds = ref(new Set());
  const validVideoStationIds = ref(new Set());

  // Add function to fetch total stations
  const fetchTotalInterviewStations = async () => {
    try {
      const questionsRef = collection(
        db,
        "new_modules/interview-course/questions",
      );
      const snapshot = await getDocs(questionsRef);

      // Track total stations and categorize them
      totalInterviewStations.value = snapshot.size;
      const categoryCount = {};
      validInterviewStationIds.value = new Set();

      snapshot.forEach((doc) => {
        const category = doc.data().category || "uncategorised";
        categoryCount[category] = (categoryCount[category] || 0) + 1;
        validInterviewStationIds.value.add(doc.id);
      });

      totalInterviewStationsByCategory.value = categoryCount;
    } catch (error) {
      console.error("Error fetching total stations:", error);
      totalInterviewStations.value = 0;
      totalInterviewStationsByCategory.value = {};
      validInterviewStationIds.value = new Set();
    }
  };

  const fetchTotalVideoStations = async () => {
    try {
      const questionsRef = collection(
        db,
        "new_modules/part-2-video-bank/questions",
      );
      const snapshot = await getDocs(questionsRef);

      totalVideoStations.value = snapshot.size;
      const categoryCount = {};
      validVideoStationIds.value = new Set();

      snapshot.forEach((doc) => {
        const category = doc.data().category || "uncategorised";
        categoryCount[category] = (categoryCount[category] || 0) + 1;
        validVideoStationIds.value.add(doc.id);
      });

      totalVideoStationsByCategory.value = categoryCount;
    } catch (error) {
      console.error("Error fetching total stations:", error);
      totalVideoStations.value = 0;
      totalVideoStationsByCategory.value = {};
      validVideoStationIds.value = new Set();
    }
  };

  // Call fetchTotalStations when the store is initialized
  fetchTotalInterviewStations();
  fetchTotalVideoStations();

  const categoriesData = useCollection(
    computed(() => {
      const path =
        bankStore.currentBank === "part-2-question-bank"
          ? "categories"
          : `new_modules/${bankStore.currentBank}/categories`;
      return collection(db, path);
    }),
  );

  // Add new function to get categories with custom path
  const getCategoriesWithPath = (collectionPath) => {
    const categoriesPath = collectionPath
      ? `${collectionPath}categories`
      : "categories";
    return useCollection(collection(db, categoriesPath));
  };

  // Add a computed ref for the sessions collection path
  const getSessionsPath = computed(() => {
    if (bankStore.currentBank === "part-2-question-bank") {
      return "sessions";
    }

    let bankPath = bankStore.currentBank;
    if (bankStore.currentBank === "interview-bank") {
      // need to change the path to interview-course for backend
      bankPath = "interview-course";
    }
    return `new_modules/${bankPath}/sessions`;
  });

  // Update sessionsData to use dynamic path
  const sessionsData = useCollection(
    computed(() => {
      const uid = currentUser.value?.uid;
      if (!uid) return null;
      return query(
        collection(db, getSessionsPath.value),
        where("userId", "==", uid),
      );
    }),
  );

  const sessionsUserIsExaminerData = useCollection(
    computed(() => {
      const email = currentUser.value?.email;
      if (!email) return null;
      return query(
        collection(db, getSessionsPath.value),
        where("examinerEmail", "==", email),
      );
    }),
  );

  let sessionsDataOrdered = ref([]);
  watch(
    [
      () => sessionsData.value.map((session) => session.status),
      () => bankStore.currentBank,
    ],
    ([newStatuses, newBank], [oldStatuses, oldBank]) => {
      if (newStatuses !== oldStatuses || newBank !== oldBank) {
        sessionsDataOrdered.value = [...sessionsData.value].sort((a, b) => {
          return b.createdAt.seconds - a.createdAt.seconds;
        });
      }
    },
    { deep: true, immediate: true },
  );

  // video bank will use the same one
  const interviewSessionsStats = computed(() => {
    // Only process when the bank is interview-bank
    if (
      bankStore.currentBank !== "interview-bank" &&
      bankStore.currentBank !== "part-2-video-bank"
    ) {
      return {};
    }

    let totalStations = 0;
    let totalStationsByCategory = {};
    let validStationIds;

    if (bankStore.currentBank === "interview-bank") {
      totalStations = totalInterviewStations.value;
      totalStationsByCategory = totalInterviewStationsByCategory.value;
      validStationIds = validInterviewStationIds.value;
    } else {
      totalStations = totalVideoStations.value;
      totalStationsByCategory = totalVideoStationsByCategory.value;
      validStationIds = validVideoStationIds.value;
    }

    const sessions = sessionsDataOrdered.value || [];
    const stationScores = {}; // Track scores for averaging
    const stationAttempts = {}; // Track number of attempts
    const stationLastAttempt = {}; // Track last attempt
    let stationSeen = new Set();
    const categoryAttempted = {}; // Track unique stations attempted per category
    const stationCategories = {}; // Track which category each station belongs to

    let totalAttempts = 0;
    let passedAttempts = 0;

    sessions.forEach((session) => {
      if (!isCurrentProgressSession(session, bankProgress.value)) return;
      const stationSummaries = Array.isArray(session.stationSummaries)
        ? session.stationSummaries
        : null;

      if (stationSummaries) {
        stationSummaries.forEach((summary) => {
          const stationId = summary.stationId;

          if (!stationId || !validStationIds.has(stationId)) return;
          if (
            !stationSummaryHasMarks(summary) &&
            !(session.attemptStatusVersion !== 1 && summary.completed === true)
          )
            return;

          const category = summary.category || "uncategorised";
          stationCategories[stationId] = category;
          stationSeen.add(stationId);

          if (!categoryAttempted[category]) {
            categoryAttempted[category] = new Set();
          }
          categoryAttempted[category].add(stationId);

          if (!stationSummaryHasMarks(summary)) return;

          const stationMaxPoints =
            typeof summary.maxPoints === "number" ? summary.maxPoints : 0;
          const percentage =
            typeof summary.percentage === "number" ? summary.percentage : 0;

          if (stationMaxPoints > 0) {
            if (!stationScores[stationId]) {
              stationScores[stationId] = [];
              stationAttempts[stationId] = 0;
            }

            stationScores[stationId].push(percentage);
            stationAttempts[stationId]++;
            if (!hasRecordedScore(stationLastAttempt, stationId)) {
              stationLastAttempt[stationId] = percentage;
            }

            totalAttempts++;
            if (percentage >= 75) {
              passedAttempts++;
            }
          }
        });
        return;
      }

      session.stations?.forEach((station) => {
        const stationId = station.stationId;

        // Skip if station doesn't exist in the database
        if (!validStationIds.has(stationId)) return;
        if (!stationHasMarks(station)) return;

        const category = station.category || "uncategorised";
        stationCategories[stationId] = category;
        stationSeen.add(stationId);

        let stationPoints = 0;
        let stationMaxPoints = 0;

        station.markSchemeSections?.forEach((section) => {
          if (!section?.checkboxes) return;

          section.checkboxes.forEach((checkbox) => {
            if (checkbox.type === "rating") {
              stationMaxPoints += 1;
              stationPoints += (checkbox.value || 0) / 3;
            } else {
              stationMaxPoints += 1;
              stationPoints += checkbox.value ? 1 : 0;
            }
          });
        });

        if (stationMaxPoints > 0) {
          // Initialize tracking objects if needed
          if (!stationScores[stationId]) {
            stationScores[stationId] = [];
            stationAttempts[stationId] = 0;
          }

          const percentage = (stationPoints / stationMaxPoints) * 100;
          stationScores[stationId].push(percentage);
          stationAttempts[stationId]++;
          // Only set last attempt if it hasn't been set yet (since we're going newest to oldest)
          if (!hasRecordedScore(stationLastAttempt, stationId)) {
            stationLastAttempt[stationId] = percentage;
          }

          // Add pass/fail tracking
          totalAttempts++;
          if (percentage >= 75) {
            passedAttempts++;
          }

          // Track unique stations attempted per category
          if (!categoryAttempted[category]) {
            categoryAttempted[category] = new Set();
          }
          categoryAttempted[category].add(stationId);
        }
      });
    });

    stationSeen = applyStationProgress(
      stationSeen,
      bankProgress.value,
      validStationIds,
    );
    for (const key of Object.keys(categoryAttempted))
      categoryAttempted[key] = new Set();
    for (const id of stationSeen) {
      const category =
        bankProgress.value?.attempts?.[id]?.category ||
        stationCategories[id] ||
        "uncategorised";
      (categoryAttempted[category] ||= new Set()).add(id);
    }

    // Calculate category stats
    const categoryStats = {};
    Object.entries(totalStationsByCategory).forEach(([category, total]) => {
      const attempted = categoryAttempted[category]?.size || 0;
      categoryStats[category] = {
        attempted,
        total,
        completionRate: (attempted / total) * 100,
      };
    });

    // Calculate average scores
    const averageScores = {};
    Object.entries(stationScores).forEach(([stationId, scores]) => {
      const sum = scores.reduce((acc, score) => acc + score, 0);
      averageScores[stationId] = sum / scores.length;
    });

    return {
      stats: {
        seen: stationSeen.size,
        unseen: totalStations - stationSeen.size,
        total: totalStations,
        attempted: Object.values(stationAttempts).reduce((a, b) => a + b, 0),
        pass: passedAttempts,
        fail: totalAttempts - passedAttempts,
      },
      categoryStats,
      stationStats: {
        seenStationIds: [...stationSeen],
        averageScores,
        lastAttemptScores: stationLastAttempt,
        attemptCounts: stationAttempts,
      },
    };
  });

  const createUserSession = async (sessionDetails) => {
    try {
      const result = await CREATE_USER_SESSION(sessionDetails);
      return result.data;
    } catch (error) {
      console.log(error.message);
      console.log(error.code);
      return {
        success: false,
        code: error.code,
        message: error.message,
        details: error.details,
      };
    }
  };

  // When a user replies to a comment, notify the parent comment author
  const notifyUserParentComment = async ({
    questionId,
    replyId,
    userId,
    parentFirstName,
    replyText,
    parentText,
    questionBank,
    draft,
  }) => {
    try {
      const result = await NOTIFY_USER_PARENT_COMMENT({
        questionId,
        replyId,
        userId,
        parentFirstName,
        replyText,
        parentText,
        questionBank,
        draft,
      });

      return result.data;
    } catch (error) {
      console.log(error.message);
      console.log(error.code);
      return { success: false };
    }
  };

  // Helper function to normalize domain titles
  const normalizeDomainTitle = (title) => {
    return (
      title
        .trim()
        .toLowerCase()
        // Remove extra spaces between words
        .replace(/\s+/g, " ")
        // Remove special characters and standardize separators
        .replace(/[-_]/g, " ")
        // Remove 's' at the end of words (communications -> communication)
        .replace(/s(\s|$)/g, "$1")
        // Remove any remaining punctuation
        .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")
        // Trim again in case any extra spaces were created
        .trim()
    );
  };

  // Add new computed property for domain stats across all sessions
  const interviewDomainStats = computed(() => {
    if (
      bankStore.currentBank !== "interview-bank" ||
      !sessionsDataOrdered.value
    )
      return {
        domains: [],
        overallAverage: 0,
      };

    // Collect all unique domains across all sessions
    const allDomains = new Set();
    const domainScores = {};
    const firstSeenDisplayName = {}; // Track the first display name we see for each normalized key

    sessionsDataOrdered.value.forEach((session) => {
      if (!isCurrentProgressSession(session, bankProgress.value)) return;
      const sectionSummaries = Array.isArray(session.sectionSummaries)
        ? session.sectionSummaries
        : null;

      if (sectionSummaries) {
        sectionSummaries.forEach((section) => {
          if (!sectionSummaryHasMarks(section)) return;
          const originalTitle = (section.title || "").trim();
          if (!originalTitle) return;
          let domainKey;
          if (originalTitle.toLowerCase().split(/\s+/)[0] === "empathy") {
            domainKey = "empathy and non verbal communication"; // Fixed key for all empathy domains
            firstSeenDisplayName[domainKey] =
              "Empathy and Non-Verbal Communication";
          } else {
            domainKey = normalizeDomainTitle(originalTitle);
          }

          if (!firstSeenDisplayName[domainKey]) {
            firstSeenDisplayName[domainKey] = originalTitle;
          }

          allDomains.add(domainKey);

          if (!domainScores[domainKey]) {
            domainScores[domainKey] = {
              scores: [],
            };
          }

          if (typeof section.percentage === "number") {
            domainScores[domainKey].scores.push(section.percentage);
          }
        });
        return;
      }

      session.stations?.forEach((station) => {
        station.markSchemeSections?.forEach((section) => {
          if (!sectionHasMarks(section)) return;
          const originalTitle = section.title.trim();
          let domainKey;
          if (originalTitle.toLowerCase().split(/\s+/)[0] === "empathy") {
            domainKey = "empathy and non verbal communication"; // Fixed key for all empathy domains
            firstSeenDisplayName[domainKey] =
              "Empathy and Non-Verbal Communication";
          } else {
            domainKey = normalizeDomainTitle(originalTitle);
          }

          // Store the first display name we see for this normalized key
          if (!firstSeenDisplayName[domainKey]) {
            firstSeenDisplayName[domainKey] = originalTitle;
          }

          allDomains.add(domainKey);

          if (!domainScores[domainKey]) {
            domainScores[domainKey] = {
              scores: [],
            };
          }

          // Calculate section score
          let points = 0;
          let maxPoints = 0;

          section.checkboxes?.forEach((checkbox) => {
            if (checkbox.type === "rating") {
              maxPoints += 1;
              points += (checkbox.value || 0) / 3;
            } else {
              maxPoints += 1;
              points += checkbox.value ? 1 : 0;
            }
          });

          if (maxPoints > 0) {
            const percentage = Math.round((points / maxPoints) * 100);
            domainScores[domainKey].scores.push(percentage);
          }
        });
      });
    });

    // Calculate overall average across all domains
    const domainStats = Array.from(allDomains)
      .map((domainKey) => {
        const { scores } = domainScores[domainKey];
        const average =
          scores.length > 0
            ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
            : 0;

        return {
          name: firstSeenDisplayName[domainKey],
          attempts: scores.length,
          average,
          scores,
        };
      })
      .sort((a, b) => b.attempts - a.attempts);

    const overallAverage =
      domainStats.length > 0
        ? Math.round(
            domainStats.reduce((sum, domain) => sum + domain.average, 0) /
              domainStats.length,
          )
        : 0;

    return {
      domains: domainStats,
      overallAverage,
    };
  });

  // Add new computed property for video bank category stats across all sessions
  const videoBankCategoryStats = computed(() => {
    if (
      bankStore.currentBank !== "part-2-video-bank" ||
      !sessionsDataOrdered.value
    ) {
      return {
        categories: [],
        overallDiagnosis: null,
        overallExamination: null,
        overallAggregate: 0,
      };
    }

    // Shared fold in @/utils/videoBankCategoryStats.mjs: every marked section
    // counts whatever its title is ("Question 1", "Management", ...); the
    // Diagnosis/Examination match only controls the split columns.
    return computeVideoBankCategoryStats(
      sessionsDataOrdered.value.filter((session) =>
        isCurrentProgressSession(session, bankProgress.value),
      ),
    );
  });

  // Function to fetch all domains with comments
  // ... existing code ...
  // ... existing code ...
  const fetchDomainsWithComments = async (bank = "part-2-video-bank") => {
    try {
      if (!currentUser.value?.uid) {
        return { domains: [], comments: [] };
      }
      // Determine the correct collection path based on the bank
      let bankPath = bank;
      if (bank === "interview-bank") {
        // Need to change the path to interview-course for backend
        bankPath = "interview-course";
      }

      const sessionsPath =
        bank === "part-2-question-bank"
          ? "sessions"
          : `new_modules/${bankPath}/sessions`;

      // Create a query to get all sessions for the current user in this bank
      const sessionsQuery = query(
        collection(db, sessionsPath),
        where("userId", "==", currentUser.value?.uid),
      );

      // Fetch the sessions
      const sessionsSnapshot = await getDocs(sessionsQuery);
      // Process the sessions to extract domains with comments
      const domainCounts = {};
      const allComments = [];

      sessionsSnapshot.forEach((doc) => {
        const session = { id: doc.id, ...doc.data() };
        const sectionComments = Array.isArray(session.sectionComments)
          ? session.sectionComments
          : null;

        if (sectionComments) {
          sectionComments.forEach((section) => {
            const comment = (section.comment || "").trim();
            if (!comment) return;

            let domainKey;
            const originalTitle = (section.title || "").trim();

            if (
              bank === "interview-bank" &&
              originalTitle.toLowerCase().split(/\s+/)[0] === "empathy"
            ) {
              domainKey = "empathy and non verbal communication";
            } else {
              domainKey = normalizeDomainTitle(originalTitle);
            }

            if (!domainCounts[domainKey]) {
              domainCounts[domainKey] = {
                name: originalTitle,
                count: 0,
                sessions: new Set(),
              };
            }

            domainCounts[domainKey].count++;
            domainCounts[domainKey].sessions.add(session.id);

            const includeComments = true;

            if (includeComments) {
              const commentObj = {
                domainKey,
                domainName: originalTitle,
                sessionId: session.id,
                sessionTitle: session.title || `Session ${session.id}`,
                sessionDate: session.createdAt
                  ? new Date(session.createdAt.seconds * 1000)
                  : null,
                stationId: section.stationId,
                stationTitle: section.stationTitle || "Unnamed Station",
                stationCategory: section.stationCategory || "",
                markingCriteria: section.title || "Unnamed Section",
                comment,
                id: `${session.id}-${section.stationId || "station"}-${
                  section.title || Math.random().toString(36).substring(2)
                }`,
              };

              allComments.push(commentObj);
            }
          });
          return;
        }

        session.stations?.forEach((station, stationIndex) => {
          station.markSchemeSections?.forEach((section, sectionIndex) => {
            if (section.comments && section.comments.trim() !== "") {
              // Normalize the section title
              let domainKey;
              const originalTitle = section.title?.trim() || "";

              if (
                bank === "interview-bank" &&
                originalTitle.toLowerCase().split(/\s+/)[0] === "empathy"
              ) {
                domainKey = "empathy and non verbal communication";
              } else {
                domainKey = normalizeDomainTitle(originalTitle);
              }

              // Track domain counts
              if (!domainCounts[domainKey]) {
                domainCounts[domainKey] = {
                  name: originalTitle,
                  count: 0,
                  sessions: new Set(),
                };
              }

              domainCounts[domainKey].count++;
              domainCounts[domainKey].sessions.add(session.id);

              // Check if includeComments is defined before using it
              const includeComments = true; // This was missing in the original code

              // If includeComments is true, collect all comments
              if (includeComments) {
                const commentObj = {
                  domainKey,
                  domainName: originalTitle,
                  sessionId: session.id,
                  sessionTitle: session.title || `Session ${session.id}`,
                  sessionDate: session.createdAt
                    ? new Date(session.createdAt.seconds * 1000)
                    : null,
                  stationId: station.id || station.stationId,
                  stationTitle: station.title || "Unnamed Station",
                  stationCategory: station.category || "",
                  markingCriteria: section.title || "Unnamed Section",
                  comment: section.comments,
                  id: `${session.id}-${station.id || station.stationId}-${
                    section.id || Math.random().toString(36).substring(2)
                  }`,
                };

                allComments.push(commentObj);
              }
            }
          });
        });
      });

      // Convert domain counts to array format
      const domainsArray = Object.entries(domainCounts).map(([key, value]) => ({
        key,
        name: value.name,
        commentCount: value.count,
        sessionCount: value.sessions.size,
      }));

      // Sort by comment count (highest first)
      const sortedDomains = domainsArray.sort(
        (a, b) => b.commentCount - a.commentCount,
      );

      // Return both domains and comments
      return {
        domains: sortedDomains,
        comments: allComments,
      };
    } catch (error) {
      console.error("Error in fetchDomainsWithComments:", error);
      return { domains: [], comments: [] };
    }
  };
  // ... existing code ...
  // ... existing code ...

  return {
    categoriesData,
    getCategoriesWithPath,
    progressPending: computed(() => bankProgress.pending.value),
    progressError: computed(() => bankProgress.error.value),
    sessionsData,
    createUserSession,
    sessionsDataOrdered,
    notifyUserParentComment,
    sessionsUserIsExaminerData,
    interviewSessionsStats,
    interviewDomainStats,
    videoBankCategoryStats,
    fetchDomainsWithComments,
  };
});
